import { useEffect, useState } from 'react'
import type {
  FileUploadStatus,
  IngestionProgress,
} from '@/shared/hooks/api/productSource'
import {
  clearIngestionDisplayPercent,
  readIngestionDisplayPercent,
  writeIngestionDisplayPercent,
} from '@/features/product-catalog/utils/ingestionDisplayPercentStore'
import { isProductSourceIngestionInFlight } from '@/features/product-catalog/utils/productSourceUploadStatus'

type IngestionStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'

const FAKE_PERCENT_HEADROOM = 14
const FAKE_PERCENT_CAP = 98
const FAKE_TICK_MIN_MS = 280
const FAKE_TICK_MAX_MS = 980
const FAKE_TICK_FACTOR_MIN = 0.01
const FAKE_TICK_FACTOR_MAX = 0.05

function isIngestionInFlight(
  status: IngestionStatus | undefined,
  fileUploadStatus: FileUploadStatus | undefined,
  filePathInBucket: string
): boolean {
  if (status == null || fileUploadStatus == null) {
    return false
  }
  return isProductSourceIngestionInFlight({
    ingestionStatus: status,
    fileUploadStatus,
    filePathInBucket,
  })
}

export function useIngestionDisplayPercent({
  sourceId,
  status,
  fileUploadStatus,
  filePathInBucket = '',
  isUploading,
  progress,
}: {
  sourceId: string
  status?: IngestionStatus
  fileUploadStatus?: FileUploadStatus
  filePathInBucket?: string
  isUploading?: boolean
  progress?: IngestionProgress | null
}): number | null {
  const serverPercent =
    progress != null && typeof progress.percent === 'number'
      ? progress.percent
      : null

  const [displayPercent, setDisplayPercent] = useState<number | null>(() =>
    readIngestionDisplayPercent(sourceId)
  )

  useEffect(() => {
    if (!isIngestionInFlight(status, fileUploadStatus, filePathInBucket)) {
      setDisplayPercent(null)
      clearIngestionDisplayPercent(sourceId)
      return
    }
    if (serverPercent != null) {
      setDisplayPercent((prev) => {
        const stored = readIngestionDisplayPercent(sourceId)
        const candidates = [serverPercent]
        if (prev != null) {
          candidates.push(prev)
        }
        if (stored != null) {
          candidates.push(stored)
        }
        const next = Math.max(...candidates)
        writeIngestionDisplayPercent(sourceId, next)
        return next
      })
    }
  }, [sourceId, status, serverPercent, fileUploadStatus, filePathInBucket])

  useEffect(() => {
    if (!isIngestionInFlight(status, fileUploadStatus, filePathInBucket)) {
      return
    }
    if (serverPercent == null) {
      return
    }
    if (isUploading) {
      return
    }

    let cancelled = false
    let timeoutId: number

    const scheduleNext = () => {
      const delay =
        FAKE_TICK_MIN_MS + Math.random() * (FAKE_TICK_MAX_MS - FAKE_TICK_MIN_MS)
      timeoutId = window.setTimeout(() => {
        if (cancelled) {
          return
        }
        setDisplayPercent((prev) => {
          const base = serverPercent
          if (prev == null) {
            writeIngestionDisplayPercent(sourceId, base)
            return base
          }
          const maxAllowed = Math.min(
            base + FAKE_PERCENT_HEADROOM,
            FAKE_PERCENT_CAP
          )
          if (prev >= maxAllowed - 0.001) {
            return prev
          }
          const distance = maxAllowed - prev
          const factor =
            FAKE_TICK_FACTOR_MIN +
            Math.random() * (FAKE_TICK_FACTOR_MAX - FAKE_TICK_FACTOR_MIN)
          const increment = distance * factor
          const next = Math.min(prev + increment, maxAllowed)
          writeIngestionDisplayPercent(sourceId, next)
          return next
        })
        scheduleNext()
      }, delay)
    }

    scheduleNext()
    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
    }
  }, [sourceId, status, serverPercent, isUploading, fileUploadStatus, filePathInBucket])

  if (!isIngestionInFlight(status, fileUploadStatus, filePathInBucket)) {
    return null
  }
  if (serverPercent == null) {
    return null
  }
  return Math.round(displayPercent ?? serverPercent)
}
